import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import {
  formateDateWithMonth,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import * as orderServices from "../../services/order.services";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";


const OrderView = () => {
  const { t } = useTranslation();
  const { id: order_id } = useParams();
  const [order, setOrder] = useState(null);
  const [loader, setLoader] = useState(true);

  const breadcrumbs = [
    { title: "Dashboard", url: "/admin/dashboard" },
    { title: "Orders", url: "/admin/order/list/1" },
    { title: `Order #${order?.order_number || "..."}`, url: "" },
  ];

  useEffect(() => {
    orderServices
      .GetOrderDetails(order_id)
      .then((res) => {
        if (res.success) {
          setOrder(res.data);
        } else {
          Swal.fire({
            icon: "error",
            text: res.message || "Failed to fetch order",
            ...SWAL_SETTINGS,
          });
        }
        setLoader(false);
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          text: "Something went wrong",
          ...SWAL_SETTINGS,
        });
        setLoader(false);
      });
  }, [order_id]);

  const manageReturn = (itemId, action) => {
    const actions = {
      approve: "Approve",
      reject: "Reject",
      process: "Process Refund",
    };

    Swal.fire({
      title: "Are you sure?",
      text: `${actions[action]} this return request?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${actions[action]}`,
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          order_id: order_id,
          item_id: itemId,
          action: action,
        };

        orderServices
          .ManageReturn(payload)
          .then((response) => {
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message || "Return status updated!",
                ...SWAL_SETTINGS,
              });
              // Refresh order data
              orderServices
                .GetOrderDetails(order_id)
                .then((res) => setOrder(res.data));
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch(() => {
            Swal.fire({
              icon: "error",
              text: "Failed to update",
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };

  const handleReturnAction = (action) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: `Change return status to "${action === "Returned" ? "Approved" : "Rejected"}"?`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: t("btn_cancel"),
      confirmButtonColor: "#403fad",
      cancelButtonColor: "#f1388b",
      confirmButtonText: t("btn_yes"),
      ...(action === "return_rejected" && {
        input: "textarea",
        inputPlaceholder: "Enter rejection reason (required)",
        inputValidator: (value) => {
          if (!value || value.trim() === "") {
            return "Please enter a valid rejection reason!";
          }
        },
      }),
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          id: order_id,
          status: action,
        };
        if (action === "return_rejected") {
          payload.admin_cancel_reason = result.value;
        }

        orderServices
          .UpdateOrderStatus(payload)
          .then((response) => {
            if (response && response.success) {
              Swal.fire({
                icon: "success",
                text: response.message || `Return ${action === "Returned" ? "approved" : "rejected"} successfully!`,
                ...SWAL_SETTINGS,
              });
              // Refresh order data
              orderServices
                .GetOrderDetails(order_id)
                .then((res) => setOrder(res.data));
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch(() => {
            Swal.fire({
              icon: "error",
              text: "Failed to update",
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };

  const handleCancelOrder = () => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: `Change order status to "Cancelled"?`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: t("btn_cancel"),
      confirmButtonColor: "#403fad",
      cancelButtonColor: "#f1388b",
      confirmButtonText: t("btn_yes"),
      input: "textarea",
      inputPlaceholder: "Enter cancellation reason (required)",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "Please enter a valid cancellation reason!";
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          id: order_id,
          status: "Cancelled",
          admin_cancel_reason: result.value,
        };

        orderServices
          .UpdateOrderStatus(payload)
          .then((response) => {
            if (response && response.success) {
              Swal.fire({
                icon: "success",
                text: response.message || "Order cancelled successfully!",
                ...SWAL_SETTINGS,
              });
              // Refresh order data
              orderServices
                .GetOrderDetails(order_id)
                .then((res) => setOrder(res.data));
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch(() => {
            Swal.fire({
              icon: "error",
              text: "Failed to update",
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };

  const handleDownloadInvoice = () => {
    if (order?.invoice_pdf) {
      window.open(order.invoice_pdf, "_blank");
    } else {
      orderServices.GetOrderInvoice(order_id).then((res) => {
        if (res && res.success && res.data?.invoice_url) {
          window.open(res.data.invoice_url, "_blank");
        } else {
          Swal.fire({
            icon: "error",
            text: (res && res.message) || "Failed to fetch invoice",
            ...SWAL_SETTINGS,
          });
        }
      });
    }
  };

  const handleResendInvoiceEmail = () => {
    Swal.fire({
      title: "Resend Invoice Email?",
      text: `Send Tax Invoice PDF to ${order?.user?.email || "user"}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Send Email",
      ...SWAL_SETTINGS,
    }).then((result) => {
      if (result.isConfirmed) {
        orderServices.ResendInvoiceEmail(order_id).then((res) => {
          if (res && res.success) {
            Swal.fire({
              icon: "success",
              text: res.message || "Invoice email sent successfully!",
              ...SWAL_SETTINGS,
            });
          } else {
            Swal.fire({
              icon: "error",
              text: (res && res.message) || "Failed to send invoice email",
              ...SWAL_SETTINGS,
            });
          }
        });
      }
    });
  };

  if (loader) return <Loader />;
  if (!order) return <div className="text-center p-5">Order not found</div>;

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="main-content-label mb-0">
                Order Details - #{order.order_number}
              </h5>
              <div>
                <button
                  className="btn btn-primary me-2"
                  onClick={handleDownloadInvoice}
                >
                  📄 View Invoice
                </button>
                {/* <button
                  className="btn btn-info me-2"
                  onClick={handleResendInvoiceEmail}
                >
                  📧 Email Invoice
                </button> */}
                {["placed", "processing", "shipped", "pending"].includes(
                  order.order_status?.toLowerCase(),
                ) && (
                  <button
                    className="btn btn-danger me-2"
                    onClick={handleCancelOrder}
                  >
                    Cancel Order
                  </button>
                )}
                {order.order_status?.toLowerCase() === "return_requested" && (
                  <>
                    <button
                      className="btn btn-warning me-2"
                      onClick={() => handleReturnAction("Returned")}
                    >
                      Approve Return
                    </button>
                    <button
                      className="btn btn-secondary me-2"
                      onClick={() => handleReturnAction("return_rejected")}
                    >
                      Reject Return
                    </button>
                  </>
                )}
                <Link to="/admin/order/list/1" className="btn btn-secondary">
                  ← Back to Orders
                </Link>
              </div>
            </div>

            <div className="row">
              {/* Order Summary */}
              <div className="col-lg-4 mb-4">
                <div className="card border shadow-sm">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">Order Summary</h6>
                  </div>
                  <div className="card-body">
                    <p>
                      <strong>Status:</strong>
                      <span
                        className={`badge ms-2 ${order.order_status === "delivered"
                          ? "bg-success"
                          : order.order_status === "cancelled"
                            ? "bg-danger"
                            : "bg-warning"
                          }`}
                      >
                        {order.order_status}
                      </span>
                    </p>
                    {order.order_status?.toLowerCase() === "cancelled" && (order.cancel_reason || order.admin_cancel_reason) && (
                      <p className="text-danger mt-2">
                        <strong>Cancellation Reason:</strong> {order.cancel_reason || order.admin_cancel_reason}
                      </p>
                    )}
                    {["return_requested", "returned", "return_rejected"].includes(order.order_status?.toLowerCase()) && (order.return_reason || order.admin_cancel_reason) && (
                      <p className={`${order.order_status?.toLowerCase() === "return_rejected" ? "text-danger" : "text-info"} mt-2`}>
                        <strong>Return Reason:</strong> {order.return_reason || order.admin_cancel_reason}
                      </p>
                    )}
                    <p>
                      <strong>Date:</strong>{" "}
                      {formateDateWithMonth(order.createdAt)}
                    </p>
                    <p>
                      <strong>Payment Status:</strong> {order.payment_status}
                    </p>
                    <p>
                      <strong>Payment Method:</strong> {order.payment_method}
                    </p>
                    <hr />
                    <p>
                      <strong>Subtotal:</strong> ₹{order.total_amount}
                    </p>
                    <p>
                      <strong>GST Amount:</strong> ₹{order.tax_amount || 0}
                    </p>
                    <p>
                      <strong>Delivery Charges:</strong> ₹
                      {order.shipping_amount}
                    </p>
                    <h5 className="text-primary">
                      <strong>Net Paid Amount:</strong> ₹{order.net_amount}
                    </h5>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="col-lg-4 mb-4">
                <div className="card border shadow-sm">
                  <div className="card-header bg-info text-white">
                    <h6 className="mb-0">Customer</h6>
                  </div>
                  <div className="card-body">
                    <p>
                      <strong>{order.user?.name || "N/A"}</strong>
                    </p>
                    <p>
                      {order.user?.phone_number || order.user?.email || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="col-lg-4 mb-4">
                <div className="card border shadow-sm">
                  <div className="card-header bg-success text-white">
                    <h6 className="mb-0">Shipping Address</h6>
                  </div>
                  <div className="card-body">
                    <p>
                      <strong>{order.full_name}</strong>
                    </p>
                    <p>{order.mobile_number}</p>
                    <p>{order.address}</p>
                    {order.landmark && (
                      <p>
                        <strong>Landmark:</strong> {order.landmark}
                      </p>
                    )}
                    <p>
                      {order.city}, {order.state} -{" "}
                      {order.pincode}
                    </p>
                    <p>{order.country}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ordered Items with Return Management */}
            <h5 className="mt-4 mb-3">Ordered Items</h5>
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Discount%</th>
                    <th>Discount Amount</th>
                    <th>GST%</th>
                    <th>GST Amount</th>
                    <th>Item Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items?.map((item, idx) => {
                    const itemImage =
                      item.sub_product?.images?.[0]?.image ||
                      item.product?.images?.[0]?.image;

                    const attributesList = item.sub_product?.attributes || [];

                    return (
                      <tr key={item.id || idx}>
                        <td>
                          <div className="d-flex align-items-center">
                            {itemImage ? (
                              <img
                                src={itemImage}
                                alt={item?.product?.name || "Product"}
                                width="60"
                                className="me-3 rounded"
                              />
                            ) : (
                              <div
                                className="bg-light border rounded me-3"
                                style={{ width: 60, height: 60 }}
                              ></div>
                            )}
                            <div>
                              <strong>{item.product?.name || "Product"}</strong>
                              {item.sub_product?.name && (
                                <div className="text-muted small">
                                  Sub-Product: {item.sub_product.name}
                                </div>
                              )}
                              {attributesList.length > 0 && (
                                <div className="mt-1">
                                  {attributesList.map((attr, aIdx) => {
                                    const title =
                                      attr.attribute?.title || attr.attribute_title || "";
                                    const val =
                                      attr.attribute_value?.value || attr.attribute_value || "";
                                    return (
                                      <span
                                        key={aIdx}
                                        className="badge bg-light text-dark border me-1 mb-1"
                                      >
                                        {title ? `${title}: ` : ""}{val}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="text-center">{item.quantity}</td>
                        <td>₹{item.price}</td>
                        <td>{item?.discount?.discount_percentage || 0}%</td>
                        <td>₹{item?.discount?.discount_amount || 0}</td>
                        <td>{item.gst_percent || item.product?.gst_percent || 0}%</td>
                        <td>₹{item.gst_amount || 0}</td>
                        <td>₹{item.price * item.quantity}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Payment Transaction */}
            {order.transaction && (
              <div className="mt-5">
                <h5>Payment Details</h5>
                <div className="card border shadow-sm">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p>
                          <strong>Amount Paid:</strong> ₹
                          {order.transaction.amount}
                        </p>
                        <p>
                          <strong>Status:</strong>
                          <span
                            className={`badge ms-2 ${order.transaction.payment_status === "success" ? "bg-success" : "bg-danger"}`}
                          >
                            {order.transaction.payment_status}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p>
                          <strong>Method:</strong> {order.transaction?.payment_method}
                        </p>
                        <p>
                          <strong>Transaction ID:</strong>{" "}
                          {order.transaction?.transaction_id || "N/A"}
                        </p>
                        <p>
                          <strong>Date:</strong>{" "}
                          {formateDateWithMonth(order.transaction?.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderView;
